import React from 'react';
import PropTypes from 'prop-types';
import _isEmpty from 'lodash/isEmpty';
import styled from 'styled-components';

import Col from 'ravenjs/lib/Col';
import Row from 'ravenjs/lib/Row';
import { calculateLastPage } from 'ravenjs/utils/pagination';
import { getThemeProps } from 'ravenjs/utils/theme';
import { get } from 'ravenjs/utils/lodash';

import { DEFAULT_COLORS } from 'constants/colors';

import PaginationActions from './PaginationActions';
import PaginationTotalRows from './PaginationTotalRows';
import PaginationTotalSelected from './PaginationTotalSelected';
import PaginationRowsPerPage from './PaginationRowsPerPage';

const PaginationControlsStyled = styled.div`
    align-items: center;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
`;

const PaginationStyled = styled.div`
    align-items: flex-start;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    padding: 20px 0;
    @media (min-width: 641px) {
        align-items: center;
        flex-direction: row;
    }
    ${getThemeProps('Pagination.styles')};
    ${({ theme }) =>
        theme.media.down('md')`
        background-color: ${DEFAULT_COLORS.COMPONENTS_TABLE_TITLE_ROW}
    `};
`;

const PaginationV2 = ({
    compress,
    currentPage,
    hideSelectedRow,
    rowsPerPage,
    rowsPerPageLabel,
    rowsPerPageOptions,
    style,
    totalRows,
    totalRowsLabel,
    onChangePage,
    selected,
    showcaseCount,
    paginationButtonSize,
    buttonColor,
    innerButtonColor,
    enableRowsPerPageOptions,
    menuPlacement,
    onRowsChange,
    renderGotoPage,
    usePaginationV2,
}) => {
    const selectedCount = !_isEmpty(selected) ? get(selected, 'itemCount', 0) : null;
    const isSelectedCountPresent = typeof selectedCount === 'number';
    const hasPages = calculateLastPage(totalRows, rowsPerPage) > 1;

    return (
        <PaginationStyled style={style}>
            <Row alignItems="center" gutter={false} width="100%" display="flex">
                {!hideSelectedRow && (
                    <Col display={{ xs: 'none', md: 'block' }} gutter={false} flex="1 1 auto">
                        <Row direction="row" gutter={false} height="100%" justify="flex-start">
                            {!_isEmpty(totalRowsLabel) && (
                                <PaginationTotalRows
                                    label={totalRowsLabel}
                                    totalRows={totalRows}
                                    usePaginationV2={usePaginationV2}
                                />
                            )}
                            {isSelectedCountPresent && (
                                <PaginationTotalSelected count={selectedCount} {...selected} />
                            )}
                        </Row>
                    </Col>
                )}
                {hasPages && (
                    <Col gutter={false} margin="0.5rem 0" flex="2 1 auto">
                        <PaginationControlsStyled>
                            <PaginationActions
                                paginationButtonSize={paginationButtonSize}
                                buttonColor={buttonColor}
                                innerButtonColor={innerButtonColor}
                                currentPage={currentPage}
                                onChangePage={onChangePage}
                                rowsPerPage={rowsPerPage}
                                showcaseCount={showcaseCount}
                                totalRows={totalRows}
                                compress={compress}
                            />
                            {enableRowsPerPageOptions && !_isEmpty(rowsPerPageOptions) && (
                                <Col
                                    display={{ xs: 'none', md: 'block' }}
                                    gutter={false}
                                    flex="1 1 auto"
                                >
                                    <PaginationRowsPerPage
                                        label={rowsPerPageLabel}
                                        onChange={onRowsChange}
                                        rowsPerPage={rowsPerPage}
                                        rowsPerPageOptions={rowsPerPageOptions}
                                        menuPlacement={menuPlacement}
                                    />
                                </Col>
                            )}
                            {renderGotoPage()}
                        </PaginationControlsStyled>
                    </Col>
                )}
            </Row>
        </PaginationStyled>
    );
};

PaginationV2.propTypes = {
    compress: PropTypes.string,
    currentPage: PropTypes.number,
    hideSelectedRow: PropTypes.bool,
    rowsPerPage: PropTypes.number,
    rowsPerPageLabel: PropTypes.node,
    rowsPerPageOptions: PropTypes.array,
    style: PropTypes.object,
    totalRows: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    totalRowsLabel: PropTypes.oneOfType([
        PropTypes.shape({
            prefix: PropTypes.node,
            suffix: PropTypes.node,
        }),
        PropTypes.string,
    ]),
    onChangePage: PropTypes.func,
    selected: PropTypes.object,
    showcaseCount: PropTypes.number,
    paginationButtonSize: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    buttonColor: PropTypes.string,
    innerButtonColor: PropTypes.string,
    enableRowsPerPageOptions: PropTypes.bool,
    menuPlacement: PropTypes.string,
    onRowsChange: PropTypes.func,
    renderGotoPage: PropTypes.func,
    usePaginationV2: PropTypes.bool,
};

PaginationV2.defaultProps = {
    paginationButtonSize: '20',
    buttonColor: DEFAULT_COLORS.BLUE,
    innerButtonColor: DEFAULT_COLORS.BLACK,
    compress: '',
    currentPage: 0,
    hideSelectedRow: false,
    onChangePage: () => {},
    rowsPerPage: 10,
    rowsPerPageLabel: 'Rows per page:',
    rowsPerPageOptions: [5, 10, 15, 20],
    enableRowsPerPageOptions: false,
    selected: {},
    showcaseCount: 4,
    style: null,
    totalRows: 0,
    totalRowsLabel: null,
    menuPlacement: 'auto',
    onRowsChange: () => {},
    renderGotoPage: () => null,
    usePaginationV2: false,
};

export default PaginationV2;
